import React, { useState } from "react";
import { Link } from "react-router-dom";
import { MailCheck } from "lucide-react";
import api from "@/lib/api";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
    } catch {
      // Non-enumerating by design — always show the same neutral confirmation.
    } finally {
      setSent(true);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#05060a] text-white p-6">
      <div className="w-full max-w-sm animate-rise">
        <div className="mb-8 flex justify-center"><Logo size="lg" /></div>
        {sent ? (
          <div className="text-center" data-testid="forgot-sent">
            <div className="mx-auto mb-4 w-14 h-14 rounded-full bg-[#1f6feb]/15 flex items-center justify-center">
              <MailCheck className="w-7 h-7 text-[#3b9eff]" />
            </div>
            <h1 className="font-display text-2xl font-bold mb-2">Check your inbox</h1>
            <p className="text-white/50 text-sm mb-6">
              If an account exists for <b className="text-white/80">{email}</b>, we've sent a password-reset link.
              It expires in 60 minutes.
            </p>
            <button onClick={() => setSent(false)} className="text-[#3b9eff] font-semibold text-sm" data-testid="forgot-resend">
              Use a different email
            </button>
            <p className="mt-6 text-sm text-white/50">
              <Link to="/login" className="text-[#3b9eff] font-semibold" data-testid="back-to-login">Back to log in</Link>
            </p>
          </div>
        ) : (
          <>
            <h1 className="font-display text-3xl font-bold mb-1">Forgot password?</h1>
            <p className="text-white/50 mb-8 text-sm">Enter your account email and we'll send you a reset link.</p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" data-testid="forgot-email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com" required className="mt-1.5 bg-white/5 border-white/10" />
              </div>
              <Button type="submit" disabled={loading} data-testid="forgot-submit"
                className="w-full rounded-full py-6 bg-gradient-to-r from-[#1f6feb] to-[#3b9eff] hover:opacity-90 brand-glow">
                {loading ? "Sending…" : "Send reset link"}
              </Button>
            </form>
            <p className="mt-6 text-sm text-white/50 text-center">
              Remembered it? <Link to="/login" className="text-[#3b9eff] font-semibold" data-testid="back-to-login">Back to log in</Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
